import { useEffect } from 'react'
import { uniqueId } from 'lodash'
import fastdom from 'fastdom'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	ComponentsLayoutChangeCallback,
	DSViewModeAPISymbol,
	EditorFeatureConfigSymbol,
	ILogger,
	LOADING_PHASES,
	LoggerSymbol,
	NavigationInfoSymbol,
	ViewerManagerStructureAPISymbol,
	IViewMode,
	ILayoutChangeObserver,
	LayoutHook,
	Experiments,
	ExperimentsSymbol,
} from '@wix/thunderbolt-symbols'
import type { ViewerAPI } from 'thunderbolt-viewer-manager-types'
import type { INavigationInfo } from 'ds-feature-editor-navigation'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import type { ILayoutUtils, ViewerManagerLayoutApiEditorConfig } from '../types'
import { LayoutUtilsSymbol, name } from '../symbols'
import { getInflatedAncestors } from '../utils'
import { initLayoutNotifier } from './LayoutNotifier'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'
import { FontsStatusNotifierSymbol, IFontsStatusNotifier } from 'ds-feature-viewer-manager-fonts-api'
import { getFullId } from '@wix/thunderbolt-commons'

export const LayoutChangeObserver = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		NavigationManagerSymbol,
		NavigationInfoSymbol,
		ViewerManagerStructureAPISymbol,
		DSViewModeAPISymbol,
		LoggerSymbol,
		ExperimentsSymbol,
		LayoutUtilsSymbol,
		MeasuresApiSymbol,
		FontsStatusNotifierSymbol,
	],
	(
		config: ViewerManagerLayoutApiEditorConfig,
		window: NonNullable<BrowserWindow>,
		navigationManager: INavigationManager,
		navigationInfo: INavigationInfo,
		structureAPI: ViewerAPI['structure'],
		viewMode: IViewMode,
		logger: ILogger,
		experiments: Experiments,
		layoutUtils: ILayoutUtils,
		measuresApi: IMeasuresApi,
		fontsStatusNotifier: IFontsStatusNotifier
	): ILayoutChangeObserver & LayoutHook => {
		const observedComponentsSet = new Set<HTMLElement>()
		const compsToNotify = new Set<string>()
		let observerEnabled: boolean = true
		let isLayoutPending: boolean = false
		let dispatchBeforePaint: boolean = false
		let isWaitingForDispatch: boolean = false
		let lastDisabled: number = 0

		const { ResizeObserver } = window

		const layoutNotifier = initLayoutNotifier<ComponentsLayoutChangeCallback>()

		const addCompToLayoutChangeNotifier = (compId: string) => {
			compsToNotify.add(compId)
			const compType = structureAPI.getStructure(compId)?.componentType || ''
			if ('WRichText' === compType.split('.').pop()) {
				// WRichText may increase its height by inner resize observer.
				// In this case the container's resize observer will be triggered in the next animation frame.
				// We have to ensure that WRichText and its ancestors are added in the same animation frame, therefore we add them manually
				const ancestors = getInflatedAncestors(compId, config.compIdToAncestors, structureAPI)
				ancestors.map((ancestorId) => compsToNotify.add(ancestorId))
			}
		}

		const shouldPreventNotify = () =>
			navigationInfo.isDuringInitialNavigationPhase() ||
			navigationManager.isDuringNavigation() ||
			viewMode.isDuringViewModeSwitch() ||
			(experiments['specs.thunderbolt.waitForFontsToLoad'] && fontsStatusNotifier.hasPendingFonts()) ||
			!observerEnabled

		const notifyObservers = () => {
			const dispatch = () => {
				// Prevent dispatch callbacks that were added when before renderDone was disabled.
				if (shouldPreventNotify()) {
					isLayoutPending = false
					return
				}

				const renderedCompsIds = config.shouldAccumulateComps
					? []
					: Array.from(layoutUtils.getRenderedComps(Array.from(compsToNotify)))
				const displayedCompIds = renderedCompsIds.filter(
					(compId) => !config.compsToIgnoreChanges[compId] && layoutUtils.isDisplayed(compId)
				)
				const dispatchId = uniqueId('layoutDoneDispatchId-')
				// Internal handlers should be triggered before external handlers (handler that registered using layoutApi)
				logger.phaseStarted(LOADING_PHASES.DS_INVOKE_LAYOUT_CHANGE_HANDLERS)
				layoutNotifier.notify([...displayedCompIds], dispatchId)
				logger.phaseEnded(LOADING_PHASES.DS_INVOKE_LAYOUT_CHANGE_HANDLERS)

				if (!config.shouldAccumulateComps) {
					compsToNotify.clear()
				}
				isLayoutPending = false
			}

			if (dispatchBeforePaint) {
				// measure comps before paint (before changes are applied to the DOM)
				// needed for scenarios like drag/resize and push where we need to measure pushed components before dragged component is updated with new mesh calc
				dispatch()
			} else {
				if (!isWaitingForDispatch) {
					isWaitingForDispatch = true
					fastdom.mutate(() => {
						fastdom.measure(() => {
							dispatch()
							isWaitingForDispatch = false
						})
					})
				}
			}

			dispatchBeforePaint = false
		}

		const notifyIfNeeded = () => {
			if (shouldPreventNotify()) {
				isLayoutPending = false
				return
			}
			isLayoutPending = true
			notifyObservers()
		}

		const getElementById = (id: string): HTMLElement | null => {
			if (!experiments['specs.thunderbolt.layoutObserverTwoElements']) {
				return measuresApi.getCompElement(id)
			}
			const element = measuresApi.getCompElement(id)
			if (!element) {
				return null
			}

			// There is a moment during rendering which the components appears twice in DOM (after reparent)
			// in this case we need to take the new element and ignore the registered one
			const isAlreadyRegistered = observedComponentsSet.has(element)

			if (!isAlreadyRegistered) {
				return element
			}

			const elementWithSameId = window.document.querySelectorAll<HTMLElement>(
				`#${measuresApi.getCompIdSelectorForMesh(id)}`
			)[1]

			return elementWithSameId || null
		}

		const useObserveComponentResize = (id: string) => {
			useEffect(() => {
				const element = getElementById(id)
				if (!element || !config.compsToUseResizeObserver[getFullId(id)]) {
					return
				}
				const resizeObserver = new ResizeObserver(() => {
					addCompToLayoutChangeNotifier(id)
					notifyIfNeeded()
				})
				resizeObserver.observe(element)
				observedComponentsSet.add(element)

				return () => {
					resizeObserver.disconnect()
					observedComponentsSet.delete(element)
				}
			}, [id])
		}

		const tryNotifyOnNavigationEnd = async () => {
			if (navigationManager.isDuringNavigation()) {
				await navigationManager.waitForNavigationEnd()
			}
			notifyIfNeeded()
		}
		navigationManager.registerToNavigationStart(tryNotifyOnNavigationEnd)

		const tryNotifyOnSwitchViewModeDone = () => {
			notifyIfNeeded()
		}
		viewMode.registerToSwitchViewModeDone(tryNotifyOnSwitchViewModeDone)

		const tryNotifyOnFontsLoaded = async () => {
			notifyIfNeeded()
		}
		if (experiments['specs.thunderbolt.waitForFontsToLoad']) {
			fontsStatusNotifier.registerToFontsLoadingStatus(tryNotifyOnFontsLoaded)
		}

		return {
			subscribe: layoutNotifier.subscribe,
			unsubscribe: layoutNotifier.unsubscribe,
			getDiagnosticState: () => ({
				CLC_compsToNotify: compsToNotify,
				CLC_isLayoutPending: isLayoutPending,
				CLC_layoutDoneEnabled: observerEnabled,
				CLC_layoutDisabledXSecondsAgo: lastDisabled ? Math.round((Date.now() - lastDisabled) / 1000) : 0,
			}),
			useObserver(compId) {
				useObserveComponentResize(compId)
			},
			addToChangedComponents: (compId) => {
				if (layoutUtils.isStructuralComp(compId)) {
					addCompToLayoutChangeNotifier(compId)
				}
			},
			addToChangedComponentsAndNotify: (compId) => {
				if (layoutUtils.isStructuralComp(compId)) {
					addCompToLayoutChangeNotifier(compId)
					notifyIfNeeded()
				}
			},
			notifyIfNeeded,
			enableLayoutChangeObserver: (isEnabled) => {
				if (isEnabled !== observerEnabled) {
					lastDisabled = isEnabled ? 0 : Date.now()
				}
				observerEnabled = isEnabled

				if (isEnabled) {
					logger.phaseStarted(LOADING_PHASES.DS_NOTIFY_LAYOUT_CHANGE)
					notifyIfNeeded()
					logger.phaseEnded(LOADING_PHASES.DS_NOTIFY_LAYOUT_CHANGE)
				} else {
					isLayoutPending = false
				}
			},
			dispatchNextLayoutDoneBeforePaint() {
				dispatchBeforePaint = true
			},
		}
	}
)
